import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import { FaExternalLinkAlt, FaHandshake } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";

import { getAllPartenaires } from "../../../services/partenairesServices";
import { resolveMediaUrl } from "./homeUtils";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./PartenairesCarousel.css";

const PartenairesCarousel = () => {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPartenaires = async () => {
      try {
        setLoading(true);

        const allPartenaires = await getAllPartenaires();

        const activePartenaires = Array.isArray(allPartenaires)
          ? allPartenaires.filter((partenaire) => partenaire.is_active)
          : [];

        if (mounted) setPartenaires(activePartenaires);
      } catch (error) {
        console.error("Erreur lors de la récupération des partenaires:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPartenaires();

    return () => {
      mounted = false;
    };
  }, []);

  const sliderSettings = useMemo(() => {
    const baseSettings = {
      infinite: partenaires.length > 4,
      slidesToShow: Math.min(4, partenaires.length || 1),
      slidesToScroll: 1,
      autoplay: partenaires.length > 1,
      speed: 900,
      autoplaySpeed: 2800,
      cssEase: "ease",
      arrows: true,
      dots: true,
      pauseOnHover: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(3, partenaires.length || 1),
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, partenaires.length || 1),
            arrows: false,
          },
        },
        {
          breakpoint: 520,
          settings: {
            slidesToShow: 1,
            arrows: false,
          },
        },
      ],
    };

    if (partenaires.length <= 1) {
      return {
        ...baseSettings,
        infinite: false,
        autoplay: false,
        dots: false,
        arrows: false,
        slidesToShow: 1,
      };
    }

    return baseSettings;
  }, [partenaires.length]);

  if (!loading && partenaires.length === 0) {
    return null;
  }

  return (
    <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Partenaires"
          title="Ils soutiennent "
          highlight="la vie associative"
          subtitle="Entreprises, institutions, commerces ou acteurs locaux : valorisez les partenaires qui accompagnent les associations de la commune."
        />

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
            Chargement des partenaires...
          </div>
        ) : (
          <Reveal>
            <div className="partner-carousel rounded-[2rem] border border-slate-200 bg-slate-50 px-4 py-8 shadow-sm dark:border-white/10 dark:bg-slate-950 sm:px-8">
              <Slider {...sliderSettings}>
                {partenaires.map((partenaire) => (
                  <div key={partenaire.id} className="px-3 py-2">
                    <PartnerCard partenaire={partenaire} />
                  </div>
                ))}
              </Slider>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

function PartnerCard({ partenaire }) {
  const logoUrl = resolveMediaUrl(
    partenaire.photo_url,
    "/img/placeholder-partenaire.png",
  );

  const content = (
    <div className="group flex min-h-[230px] flex-col items-center justify-center rounded-[1.5rem] border border-slate-200 bg-white p-6 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
      <div className="mb-5 flex h-24 w-full items-center justify-center">
        <img
          src={logoUrl}
          alt={partenaire.nom}
          className="max-h-24 w-full object-contain transition duration-300 group-hover:scale-105"
        />
      </div>

      <h3 className="text-base font-bold text-slate-950 dark:text-white">
        {partenaire.nom}
      </h3>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-primary">
        <FaHandshake />
        Partenaire
      </div>

      {partenaire.link_url && (
        <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-slate-400 transition group-hover:text-primary">
          Voir le site
          <FaExternalLinkAlt />
        </span>
      )}
    </div>
  );

  if (!partenaire.link_url) {
    return content;
  }

  return (
    <a
      href={partenaire.link_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      aria-label={`Voir le site du partenaire ${partenaire.nom}`}
    >
      {content}
    </a>
  );
}

export default PartenairesCarousel;
